import { useCallback, useEffect, useRef } from 'react';

import { useTranslation } from 'react-i18next';

import { FilterSearch } from '@/components/data-table/filters-v1';
import { DataTableFiltersV2 } from '@/components/data-table/filters-v2';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { GroupBy } from '@/modules/common';
import { logger } from '@/utils/logger/logger';

import {
  DateSwitcherHeader,
  Planner,
  PlannerPreferencesDropdown,
  PlannerZoomControl,
} from '../components';
import { PLANNER_DATA_LOG_TAG } from '../constants';
import { TextMeasurementProvider } from '../contexts/text-measurement-context';
import { usePlannerBackgroundRefresh, usePlannerPageFilters } from '../hooks';
import { usePlannerAssignments } from '../hooks/assignments-data-load/use-planner-assignments';
import usePlannerStore from '../store';
import { usePlannerStoreEffects } from '../store/effects';

export const PlannerPage = () => {
  const { t } = useTranslation(['authenticated.planner', 'authenticated.projects']);
  // Store side effects (cascade toasts, scroll commands) — slices only describe
  // them as state; this mounts the subscribers that execute them.
  usePlannerStoreEffects();
  const groupBy = usePlannerStore(store => store.groupBy);
  const setGroupBy = usePlannerStore(store => store.setGroupBy);
  const setPlannerData = usePlannerStore(store => store.setPlannerData);
  const beginUiTransition = usePlannerStore(store => store.beginUiTransition);
  const endUiTransition = usePlannerStore(store => store.endUiTransition);

  const { filters, debouncedFilters, filterState, filtersConfig, searchFilterConfig, searchValue } =
    usePlannerPageFilters();

  const {
    rows,
    assignments,
    timeOffs,
    isLoading,
    isRefreshing,
    error,
    fetchNextPage,
    fetchPreviousDateRange,
    fetchNextDateRange,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = usePlannerAssignments({
    groupBy,
    filters: debouncedFilters,
  });

  // Keep shared planner data fresh (focus / periodic / on navigation in) without
  // a loading skeleton, filter reset, or date jump — the cache itself is kept
  // alive for the whole session (gcTime Infinity) to avoid stale-cache mis-renders.
  usePlannerBackgroundRefresh();

  const pendingGroupByRef = useRef<GroupBy | null>(null);
  const groupByRafRef = useRef<number | null>(null);

  useEffect(() => {
    // Apply rows + assignments + timeOffs in one atomic store update. Using separate
    // setters left a render window where newly-paginated rows existed without their
    // assignments, so their bars rendered blank until an unrelated re-render (Bug:
    // assignments missing during vertical pagination).
    setPlannerData({ rows, assignments, timeOffs });

    logger.debug(
      PLANNER_DATA_LOG_TAG,
      'setPlannerData rows=' +
        rows.length +
        ' assignmentRows=' +
        assignments.size +
        ' timeOffRows=' +
        timeOffs.size
    );
  }, [rows, assignments, timeOffs, setPlannerData]);

  const handleGroupByChange = useCallback(
    (value: string) => {
      const nextGroupBy = value as GroupBy;
      if (nextGroupBy === groupBy) return;

      if (!pendingGroupByRef.current) {
        beginUiTransition();
      }
      pendingGroupByRef.current = nextGroupBy;

      if (groupByRafRef.current !== null) {
        cancelAnimationFrame(groupByRafRef.current);
      }

      groupByRafRef.current = requestAnimationFrame(() => {
        groupByRafRef.current = null;
        setGroupBy(nextGroupBy);
      });
    },
    [groupBy, setGroupBy, beginUiTransition]
  );

  useEffect(() => {
    const pendingGroupBy = pendingGroupByRef.current;
    if (!pendingGroupBy) return;

    // Keep the lightweight overlay visible while first load/transform for the selected
    // grouping is in progress.
    if (groupBy === pendingGroupBy && !isLoading) {
      pendingGroupByRef.current = null;
      endUiTransition();
    }
  }, [groupBy, isLoading, endUiTransition]);

  useEffect(() => {
    return () => {
      if (groupByRafRef.current !== null) {
        cancelAnimationFrame(groupByRafRef.current);
      }
      if (pendingGroupByRef.current) {
        pendingGroupByRef.current = null;
        endUiTransition();
      }
    };
  }, [endUiTransition]);

  // Cleanup large data structures on unmount to free memory
  useEffect(() => {
    return () => {
      // Clear rows + assignments + timeOffs to release memory when navigating away
      setPlannerData({ rows: [], assignments: new Map(), timeOffs: new Map() });
    };
  }, [setPlannerData]);

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header: title + group tabs + preferences + minimize toggle */}
      <div className="shrink-0 bg-surface px-4 pt-4 shadow-[0_1px_4px_0_hsl(var(--border))]">
        <div className="flex w-full items-center justify-between pb-4">
          <h1 className="text-2xl font-semibold">{t('title')}</h1>
          <div className="flex items-center gap-3">
            <Tabs defaultValue={GroupBy.USERS} value={groupBy} onValueChange={handleGroupByChange}>
              <TabsList className="bg-border">
                <TabsTrigger className="transition-none" value={GroupBy.USERS}>
                  {t('user')}
                </TabsTrigger>
                <TabsTrigger className="transition-none" value={GroupBy.DEPARTMENTS}>
                  {t('department')}
                </TabsTrigger>
                <TabsTrigger className="transition-none" value={GroupBy.PROJECTS}>
                  {t('project')}
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <PlannerPreferencesDropdown />
            <PlannerZoomControl />
          </div>
        </div>
      </div>
      <Separator />
      {/* Filters + date switcher below the header */}
      <div className="flex shrink-0 items-start justify-between gap-4 px-4 py-3">
        {/* The search input rides inside the filters' wrap row (leadingControls)
            so on narrow screens chips wrap under it, not under the filter button */}
        <DataTableFiltersV2
          filtersConfig={filtersConfig}
          filterState={filterState}
          tableId="planner"
          leadingControls={
            <FilterSearch
              config={searchFilterConfig}
              value={searchValue}
              onChange={(key, value) => filterState.setFilter(key, value)}
            />
          }
        />
        <DateSwitcherHeader />
      </div>
      <div className="min-h-0 flex-1">
        <TextMeasurementProvider>
          <Planner
            rows={rows}
            assignments={assignments}
            filters={filters}
            isLoading={isLoading}
            isRefreshing={isRefreshing}
            error={error}
            onRefresh={refetch}
            onLoadDatesLeft={fetchPreviousDateRange}
            onLoadDatesRight={fetchNextDateRange}
            onLoadMoreRows={fetchNextPage}
            hasMoreRows={hasNextPage}
            isLoadingMoreRows={isFetchingNextPage}
          />
        </TextMeasurementProvider>
      </div>
    </div>
  );
};
